import { useEffect, useState } from "react";
import api, { fmtError, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Download, FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CRM_FIELDS_CLIENTS = [
  { v: "company_name", l: "Company name *" },
  { v: "contact_person", l: "Contact person" },
  { v: "phone", l: "Phone" },
  { v: "email", l: "Email" },
  { v: "email_or_website", l: "Email / Website (combined)" },
  { v: "website", l: "Website" },
  { v: "gst_number", l: "GST number (GSTIN)" },
  { v: "industry", l: "Industry" },
  { v: "location", l: "Location" },
  { v: "billing_attention", l: "Billing — Attention" },
  { v: "billing_line1", l: "Billing — Address (line 1)" },
  { v: "billing_street2", l: "Billing — Street 2" },
  { v: "billing_city", l: "Billing — City" },
  { v: "billing_state", l: "Billing — State" },
  { v: "billing_phone", l: "Billing — Phone" },
  { v: "place_of_supply", l: "Place of supply (with state code)" },
  { v: "state_code", l: "State code" },
  { v: "state_name", l: "State name" },
  { v: "customer_type", l: "Customer type" },
  { v: "notes", l: "Notes" },
];

// Header → CRM field map (case-insensitive, punctuation-insensitive lookup).
const CLIENT_HEADER_ALIASES = {
  "company name": "company_name", company: "company_name", name: "company_name",
  "contact person": "contact_person", contact: "contact_person", "primary contact": "contact_person",
  phone: "phone", "primary phone": "phone", mobile: "phone",
  email: "email", "email id": "email",
  website: "website",
  "email id website": "email_or_website", "email website": "email_or_website",
  "email id  website": "email_or_website", "email id/website": "email_or_website",
  "billing attention": "billing_attention", attention: "billing_attention",
  "billing address": "billing_line1", "billing line 1": "billing_line1", "billing address line 1": "billing_line1",
  "billing street2": "billing_street2", "billing street 2": "billing_street2", "billing line 2": "billing_street2",
  "billing city": "billing_city",
  "billing state": "billing_state",
  "billing phone": "billing_phone",
  "gst identification number gstin": "gst_number", "gst identification number": "gst_number",
  gstin: "gst_number", gst: "gst_number", "gst number": "gst_number", "gst no": "gst_number",
  "place of contact with state code": "place_of_supply", "place of contact": "place_of_supply",
  "place of supply": "place_of_supply",
  "state code": "state_code", "state name": "state_name",
  industry: "industry", location: "location", city: "location",
  type: "customer_type", "customer type": "customer_type", category: "customer_type",
  notes: "notes",
};

const normHeader = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const CRM_FIELDS_LEADS = [
  { v: "name", l: "Name *" },
  { v: "designation", l: "Designation" },
  { v: "company_name", l: "Company" },
  { v: "email", l: "Email" },
  { v: "phone", l: "Phone" },
  { v: "source", l: "Source" },
  { v: "status", l: "Status" },
  { v: "event_name", l: "Event / campaign name" },
  { v: "industry", l: "Industry" },
  { v: "location", l: "Location" },
  { v: "notes", l: "Notes" },
];

const CRM_FIELDS_PRODUCTS = [
  { v: "name", l: "Name *" },
  { v: "sku", l: "SKU / Code" },
  { v: "category", l: "Category" },
  { v: "description", l: "Description" },
  { v: "unit_price", l: "Unit price" },
  { v: "currency", l: "Currency" },
  { v: "uom", l: "Unit of measure (UOM)" },
  { v: "supplier", l: "Supplier" },
  { v: "roi_benefits", l: "ROI / benefits" },
];

const CRM_FIELDS_SERVICES = [
  { v: "name", l: "Name *" },
  { v: "type", l: "Type" },
  { v: "description", l: "Description" },
  { v: "duration", l: "Duration" },
  { v: "unit_price", l: "Unit price" },
  { v: "currency", l: "Currency" },
  { v: "uom", l: "Unit of measure (UOM)" },
  { v: "sku", l: "Code / SKU" },
  { v: "benefits", l: "Benefits" },
];

const SERVICE_HEADER_ALIASES = {
  name: "name", "service name": "name", "training name": "name", "course name": "name", item: "name", "item name": "name",
  type: "type", category: "type",
  description: "description", desc: "description", details: "description",
  duration: "duration", days: "duration",
  "unit price": "unit_price", price: "unit_price", rate: "unit_price", fee: "unit_price",
  currency: "currency",
  uom: "uom", unit: "uom",
  sku: "sku", code: "sku", "service code": "sku", "course code": "sku",
  benefits: "benefits",
};

const CRM_FIELDS_DEALS = [
  { v: "title", l: "Deal title *" },
  { v: "company_name", l: "Company name * (auto-links client)" },
  { v: "value", l: "Deal value (₹)" },
  { v: "stage", l: "Stage" },
  { v: "expected_close_date", l: "Expected close date" },
  { v: "notes", l: "Notes" },
];

const DEAL_HEADER_ALIASES = {
  title: "title", "deal title": "title", "deal name": "title", opportunity: "title", "opportunity name": "title",
  company: "company_name", "company name": "company_name", client: "company_name", account: "company_name", "client name": "company_name",
  value: "value", amount: "value", "deal value": "value", "deal amount": "value",
  stage: "stage", "deal stage": "stage", "pipeline stage": "stage",
  "expected close date": "expected_close_date", "close date": "expected_close_date", "expected close": "expected_close_date",
  notes: "notes", remarks: "notes",
};

const CRM_FIELDS_CONTACTS = [
  { v: "name", l: "Name *" },
  { v: "designation", l: "Designation" },
  { v: "company_name", l: "Company name (auto-links to client)" },
  { v: "email", l: "Email" },
  { v: "phone", l: "Phone" },
  { v: "linkedin", l: "LinkedIn URL" },
  { v: "notes", l: "Notes" },
];

const PRODUCT_HEADER_ALIASES = {
  name: "name", "product name": "name", "item name": "name", item: "name", product: "name",
  sku: "sku", code: "sku", "product code": "sku", "item code": "sku", "part number": "sku", "part no": "sku",
  category: "category", type: "category",
  description: "description", desc: "description", details: "description",
  "unit price": "unit_price", price: "unit_price", rate: "unit_price", mrp: "unit_price", "list price": "unit_price",
  currency: "currency",
  uom: "uom", unit: "uom", "unit of measure": "uom",
  supplier: "supplier", vendor: "supplier", "supplier name": "supplier",
  "roi benefits": "roi_benefits", roi: "roi_benefits", benefits: "roi_benefits",
};

const CONTACT_HEADER_ALIASES = {
  name: "name", "contact name": "name", "full name": "name",
  designation: "designation", title: "designation", role: "designation", "job title": "designation",
  company: "company_name", "company name": "company_name", organisation: "company_name", organization: "company_name", account: "company_name",
  email: "email", "email id": "email", "e mail": "email",
  phone: "phone", mobile: "phone", "contact number": "phone",
  linkedin: "linkedin", "linkedin url": "linkedin", "linkedin profile": "linkedin",
  notes: "notes", remarks: "notes",
};

// Canonical lead-source list — MUST stay in sync with backend `LEAD_SOURCES`
// in /app/backend/server.py. Legacy codes are auto-translated server-side
// via LEGACY_LEAD_SOURCE_MAP for any old CSVs that still carry them.
const LEAD_SOURCES_FOR_DEFAULT = [
  ["lead_growth_plan", "Lead growth plan"],
  ["crm_contacts", "CRM contacts"],
  ["existing_user", "Existing user"],
  ["existing_user_referral", "Existing user referral"],
  ["team_referral", "Team referral"],
  ["repeat_opportunities", "Repeat opportunities"],
  ["onsite_training", "Onsite training"],
  ["product_support", "Product support"],
  ["certification_course", "Certification course"],
  ["digital_marketing", "Digital marketing"],
  ["linkedin_campaign", "LinkedIn campaign"],
  ["renviro_website", "RENVIRO website"],
  ["inbound_enquiry", "Inbound enquiry"],
  ["customer_visit", "Customer visit"],
  ["webinars", "Webinars"],
  ["exhibition_conference", "Exhibition / conference"],
  ["gem_indiamart", "GeM / IndiaMART"],
  ["cross_selling", "Cross-selling"],
  ["expansion_project", "Expansion project"],
  ["multi_location_industries", "Multi-location industries"],
  ["other_sources", "Other"],
];

function ImportFlow() {
  const [entity, setEntity] = useState("clients"); // clients | leads | products | services | training | deals | contacts
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [onDup, setOnDup] = useState("skip");
  const [defaultSource, setDefaultSource] = useState("exhibition_conference");
  const [defaultEventName, setDefaultEventName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [summary, setSummary] = useState(null);
  // Record ownership — imported clients/leads/deals belong to this user
  // (defaults to the importer, so records never silently become admin's).
  const [users, setUsers] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([])); }, []);

  const ENTITY_FIELDS = {
    clients: CRM_FIELDS_CLIENTS,
    leads: CRM_FIELDS_LEADS,
    products: CRM_FIELDS_PRODUCTS,
    services: CRM_FIELDS_SERVICES,
    training: CRM_FIELDS_SERVICES,
    deals: CRM_FIELDS_DEALS,
    contacts: CRM_FIELDS_CONTACTS,
  };
  const ENTITY_ALIASES = {
    clients: CLIENT_HEADER_ALIASES,
    leads: {}, // leads use the simpler norm-name match
    products: PRODUCT_HEADER_ALIASES,
    services: SERVICE_HEADER_ALIASES,
    training: SERVICE_HEADER_ALIASES,
    deals: DEAL_HEADER_ALIASES,
    contacts: CONTACT_HEADER_ALIASES,
  };
  const ENTITY_CONFIRM_URL = {
    clients: "/import/confirm",
    leads: "/import/leads/confirm",
    products: "/import/products/confirm",
    services: "/import/products/confirm",
    training: "/import/products/confirm",
    deals: "/import/deals/confirm",
    contacts: "/import/contacts/confirm",
  };
  const fields = ENTITY_FIELDS[entity] || [];

  // Smart guess of entity from a list of column names. Used to auto-switch
  // the tab when the user uploads (e.g.) a products sheet under the Clients
  // tab — saves them a manual click.
  const guessEntity = (cols) => {
    const norms = cols.map(normHeader);
    const has = (...keys) => keys.some((k) => norms.includes(k));
    if (has("sku", "unit price", "uom", "supplier", "product code", "part number")) return "products";
    if (has("source", "event name", "campaign", "lead status")) return "leads";
    if (has("billing address", "billing city", "billing state", "gst identification number gstin", "place of contact with state code", "gstin")) return "clients";
    if (has("designation", "linkedin", "linkedin url") && !has("billing address", "gstin")) return "contacts";
    return null;
  };

  const buildAutoMap = (cols, ent) => {
    const aliasMap = ENTITY_ALIASES[ent] || {};
    const flds = ENTITY_FIELDS[ent] || [];
    const auto = {};
    cols.forEach((col) => {
      const nh = normHeader(col);
      const aliasHit = aliasMap[nh];
      if (aliasHit) { auto[col] = aliasHit; return; }
      const norm = col.toLowerCase().replace(/[^a-z0-9]/g, "_");
      const match = flds.find((fld) => fld.v === norm || fld.l.toLowerCase().startsWith(col.toLowerCase()));
      if (match) auto[col] = match.v;
    });
    return auto;
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setSummary(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/import/preview", fd);
      // Smart entity detection — only auto-switch from the DEFAULT tab so an
      // explicit Services/Training/Deals choice is never overridden.
      const guessed = guessEntity(data.columns);
      const ent = (entity === "clients" && guessed && guessed !== entity) ? guessed : entity;
      if (ent !== entity) {
        setEntity(ent);
        toast.success(`Detected ${ent} sheet — switched tab`);
      }
      setPreview(data);
      setMapping(buildAutoMap(data.columns, ent));
    } catch (err) { toast.error(fmtError(err)); }
  };

  // Re-run auto-map whenever the user manually flips the entity tab AFTER a
  // file is already loaded (so the new entity's options take effect).
  const onEntityChange = (k) => {
    setEntity(k);
    setSummary(null);
    if (preview) {
      setMapping(buildAutoMap(preview.columns, k));
    } else {
      setMapping({});
      setFile(null);
    }
  };

  const submit = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const all = await readAllRows(file);
      const rows = all.map((r) => {
        const out = {};
        Object.entries(mapping).forEach(([col, field]) => {
          if (field && r[col] != null) out[field] = r[col];
        });
        return out;
      });
      const url = ENTITY_CONFIRM_URL[entity];
      const ownerBody = ownerId ? { owner_id: ownerId } : {};
      const body = entity === "leads"
        ? { rows, on_duplicate: onDup, default_source: defaultSource, default_event_name: defaultEventName || null, ...ownerBody }
        : ["products", "services", "training"].includes(entity)
          ? { rows, on_duplicate: onDup, target: entity }
          : { rows, on_duplicate: onDup, ...ownerBody };
      const { data } = await api.post(url, body);
      setSummary(data);
      toast.success(`Created ${data.created}, updated ${data.updated}, skipped ${data.skipped}`);
    } catch (err) { toast.error(fmtError(err)); } finally { setConfirming(false); }
  };

  return (
    <div className="space-y-5">
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">What are you uploading?</p>
          <div className="flex gap-2 flex-wrap">
            {[
              { k: "clients", label: "Clients (companies)" },
              { k: "leads", label: "Leads (event / campaign attendees)" },
              { k: "products", label: "Products" },
              { k: "services", label: "Services" },
              { k: "training", label: "Training" },
              { k: "deals", label: "Deals" },
              { k: "contacts", label: "Contacts (decision-makers)" },
            ].map((e) => (
              <button
                key={e.k}
                type="button"
                onClick={() => onEntityChange(e.k)}
                className={`tap rounded-2xl px-4 py-2.5 text-sm font-semibold transition border ${
                  entity === e.k
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/40 border-border/60 hover:bg-muted/60"
                }`}
                data-testid={`import-entity-${e.k}`}
              >
                {e.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Templates</p>
          <p className="text-xs text-muted-foreground mb-4">Download a CSV with the right columns and 2 example rows. Open in Excel, fill in your data, save as CSV, then upload below.</p>
          <div className="flex flex-wrap gap-2">
            {[
              { k: "leads", label: "Leads template" },
              { k: "clients", label: "Clients template" },
              { k: "products", label: "Products template" },
              { k: "services", label: "Services template" },
              { k: "training", label: "Training template" },
              { k: "contacts", label: "Contacts template" },
              { k: "deals", label: "Deals template" },
            ].map((t) => {
              return (
                <a
                  key={t.k}
                  href={`${(typeof window !== "undefined" ? window.location.origin : "")}/api/import/template/${t.k}`}
                  className="inline-flex items-center gap-2 rounded-2xl border border-border/60 px-4 py-2.5 text-sm font-semibold tap hover:bg-muted/50 hover:border-primary/60"
                  data-testid={`tpl-${t.k}`}
                  download
                >
                  <Download className="h-3.5 w-3.5" /> {t.label}
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 1 — Upload</p>
          <label className="flex flex-col items-center justify-center gap-3 p-10 rounded-3xl border-2 border-dashed border-border/80 bg-muted/30 cursor-pointer hover:bg-muted/50 transition tap" data-testid="import-dropzone">
            <Upload className="h-8 w-8 text-primary" />
            <span className="font-semibold">Drop Excel / CSV or click to choose</span>
            <span className="text-xs text-muted-foreground">{file ? file.name : "Max ~50k rows"}</span>
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          </label>
        </CardContent>
      </Card>

      {preview && (
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 2 — Map columns ({preview.row_count} rows)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {preview.columns.map((col) => (
                <div key={col} className="flex items-center gap-2 p-3 rounded-2xl border border-border/60">
                  <span className="font-mono text-xs bg-muted px-2 py-1 rounded-md flex-1 truncate">{col}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <Select value={mapping[col] || "__skip"} onValueChange={(v) => setMapping({ ...mapping, [col]: v === "__skip" ? "" : v })}>
                    <SelectTrigger className="rounded-xl h-9 w-44 text-xs"><SelectValue placeholder="Skip" /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__skip">Skip</SelectItem>
                      {fields.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {entity === "leads" && (
              <div className="mt-5 p-4 rounded-2xl bg-primary-50/30 border border-primary-200/60">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-700 mb-2.5">Fallback defaults (applied to rows missing these columns)</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Default source</Label>
                    <Select value={defaultSource} onValueChange={setDefaultSource}>
                      <SelectTrigger className="rounded-2xl h-10 mt-1.5 bg-white" data-testid="import-default-source"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        {LEAD_SOURCES_FOR_DEFAULT.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Default event / campaign name</Label>
                    <Input
                      className="rounded-2xl h-10 mt-1.5 bg-white"
                      value={defaultEventName}
                      onChange={(e) => setDefaultEventName(e.target.value)}
                      placeholder="e.g. ACMA Reliability Conclave 2026"
                      data-testid="import-default-event"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-5">
              <Label className="text-xs font-semibold">On duplicate</Label>
              <Select value={onDup} onValueChange={setOnDup}>
                <SelectTrigger className="rounded-2xl h-10 w-44" data-testid="import-dup-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="skip">Skip duplicate</SelectItem>
                  <SelectItem value="update">Update existing</SelectItem>
                  <SelectItem value="create_new">Create anyway</SelectItem>
                </SelectContent>
              </Select>
              {["clients", "leads", "deals"].includes(entity) && users.length > 1 && (
                <>
                  <Label className="text-xs font-semibold">Record owner</Label>
                  <Select value={ownerId || "__me"} onValueChange={(v) => setOwnerId(v === "__me" ? "" : v)}>
                    <SelectTrigger className="rounded-2xl h-10 w-48" data-testid="import-owner-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__me">Me (importer)</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </>
              )}
              <Button onClick={submit} disabled={confirming} className="rounded-2xl tap" data-testid="import-confirm-button">
                {confirming ? "Importing…" : `Import ${entity}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {summary && (
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Step 3 — Result</p>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="p-4 rounded-2xl bg-emerald-50">
                <p className="text-xs font-bold text-emerald-700">Created</p>
                <p className="font-display text-2xl sm:text-3xl font-black text-emerald-700 mt-1">{summary.created}</p>
              </div>
              <div className="p-4 rounded-2xl bg-blue-50">
                <p className="text-xs font-bold text-blue-700">Updated</p>
                <p className="font-display text-2xl sm:text-3xl font-black text-blue-700 mt-1">{summary.updated}</p>
              </div>
              <div className="p-4 rounded-2xl bg-amber-50">
                <p className="text-xs font-bold text-amber-700">Skipped</p>
                <p className="font-display text-2xl sm:text-3xl font-black text-amber-700 mt-1">{summary.skipped}</p>
              </div>
              <div className="p-4 rounded-2xl bg-red-50">
                <p className="text-xs font-bold text-red-700">Errors</p>
                <p className="font-display text-2xl sm:text-3xl font-black text-red-700 mt-1">{summary.errors.length}</p>
              </div>
            </div>
            {summary.errors.length > 0 && (
              <div className="mt-4 space-y-1 text-xs">
                {summary.errors.slice(0, 10).map((e) => (
                  <div key={`err-${e.row}`} className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-3 w-3" /> Row {e.row}: {e.reason}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

async function readAllRows(file) {
  // CSV: parse client-side to keep the upload single-trip.
  if (file.name.toLowerCase().endsWith(".csv")) {
    const txt = await file.text();
    return csvParse(txt);
  }
  // XLSX/XLS: re-upload to backend with ?full=1 so it returns every row
  // (the initial preview only ships 5 sample rows for the column mapper).
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/import/preview?full=true`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!r.ok) throw new Error(`Failed to read sheet: ${r.status}`);
  const data = await r.json();
  return data.rows || data.sample || [];
}

function csvParse(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return [];
  const headers = splitCsv(lines[0]);
  return lines.slice(1).map((line) => {
    const vals = splitCsv(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ""; });
    return obj;
  });
}
function splitCsv(line) {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { q = !q; continue; }
    if (c === "," && !q) { out.push(cur); cur = ""; continue; }
    cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function ExportPanel() {
  const download = (entity, fmt) => {
    const url = `${API}/export/${entity}?fmt=${fmt}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const entities = ["clients", "contacts", "deals", "activities"];
  return (
    <Card className="rounded-3xl border-border/60 shadow-soft">
      <CardContent className="p-6">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Download data</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {entities.map((e) => (
            <div key={e} className="p-4 rounded-2xl border border-border/60 flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              <p className="font-semibold capitalize flex-1">{e}</p>
              <Button size="sm" variant="outline" className="rounded-xl tap" onClick={() => download(e, "csv")} data-testid={`export-${e}-csv`}>CSV</Button>
              <Button size="sm" className="rounded-xl tap" onClick={() => download(e, "xlsx")} data-testid={`export-${e}-xlsx`}><Download className="h-3 w-3 mr-1" />Excel</Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ImportExport() {
  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Admin</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Import / Export.</h1>
        <p className="text-sm text-muted-foreground mt-2">Bulk-load existing customers. Download fresh exports any time.</p>
      </header>
      <Tabs defaultValue="import">
        <TabsList className="rounded-2xl mb-5">
          <TabsTrigger value="import" className="rounded-xl" data-testid="import-tab">Import customers</TabsTrigger>
          <TabsTrigger value="export" className="rounded-xl" data-testid="export-tab">Export</TabsTrigger>
        </TabsList>
        <TabsContent value="import"><ImportFlow /></TabsContent>
        <TabsContent value="export"><ExportPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
